import React, { useMemo } from 'react';
import forEach from 'lodash/forEach';

import GoogleMap from '../../components/Map/GoogleMap';
import Icon from '../../icons';
import { COLORS, STEP_TYPE } from '../../constants';

const RouteMap = ({
  mapData,
  vehicles = [],
  routes = [],
  onClickStep,
  active,
  hideDriverIds,
  allOrders = [],
  searchStopsMap = '',
  searchTicketsMap = '',
}) => {
  const { markers, polylines } = useMemo(() => {
    let newMarkers = [];
    const newPolylines = [];

    if (mapData) {
      forEach(routes, (route) => {
        const mapRoute = mapData[route.driver.id];
        if (hideDriverIds?.includes(route.driver.id)) return;
        if (!mapRoute) return;

        const steps = mapRoute.steps;
        const polyline = mapRoute.polyline;
        const color = mapRoute.color;
        forEach(steps, (step, indexStep) => {
          let className = '';
          let textColor = color;
          let backgroundColor = '';
          if (step.type === STEP_TYPE.pickup && step.status === 'COMPLETED') {
            const orderIds = step.orderIds || [];
            const isCompleted = orderIds.some((id) => {
              const order = allOrders.find((o) => o.id === id);
              return order && order.pickupStatus === 'COLLECTED';
            });
            textColor = 'white';

            if (isCompleted) {
              className = 'completed';
              backgroundColor = '#437444'; // Green for completed pickups
            } else {
              className = 'failed';
              backgroundColor = '#b03030'; // Red for failed pickups
            }
          } else if (step.type === STEP_TYPE.delivery && step.status === 'COMPLETED') {
            // For delivery, check if any order in orderIds is completed
            const orderIds = step.orderIds || (step.orderId ? [step.orderId] : []);
            const isCompleted = orderIds.some((id) => {
              const order = allOrders.find((o) => o.id === id);
              return order && order.status === 'COMPLETED';
            });
            textColor = 'white';
            if (isCompleted) {
              className = 'completed';
              backgroundColor = '#437444'; // Green for completed deliveries
            } else {
              className = 'failed';
              backgroundColor = '#b03030'; // Red for failed deliveries
            }
          }
          newMarkers.push({
            ...step,
            isUnscheduled: false,
            content: (
              <Icon
                type={step.type === STEP_TYPE.pickup ? 'MarkerPickup' : 'MarkerDropoff'}
                text={indexStep + 1}
                color={backgroundColor || '#fff'}
                strokeColor={backgroundColor ? backgroundColor : color}
                textColor={textColor}
                className={className}
              />
            ),
            latitude: Number(step.lat),
            longitude: Number(step.lng),
            offsetX: 1,
            offsetY: -6,
            duration: STEP_TYPE.pickup === step.type ? step.pickupDuration : step.deliveryDuration,
            onClick: () => onClickStep(step),
            color: color,
          });
        });
        if (polyline) {
          newPolylines.push({
            polyline: polyline,
            color: color,
            strokeWeight: 2,
            strokeOpacity: 0.8,
          });
        }
      });
      if (mapData['unscheduled']?.steps.length > 0) {
        forEach(mapData['unscheduled']?.steps, (stop, index) => {
          const stopOrder = index + 1;
          newMarkers.push({
            ...stop,
            content: (
              <Icon
                type={stop.type === STEP_TYPE.pickup ? 'MarkerPickup' : 'MarkerDropoff'}
                text={stopOrder}
                color={'#fff'}
                strokeColor={'#606060'}
              />
            ),
            isUnscheduled: true,
            latitude: Number(stop.lat),
            longitude: Number(stop.lng),
            offsetX: 1,
            offsetY: -6,
            onClick: () => onClickStep(stop),
          });
        });
      }

      if (searchStopsMap) {
        newMarkers = newMarkers.filter((marker) => {
          return (
            !marker.isUnscheduled ||
            marker.address?.toString().toLowerCase().match(searchStopsMap.toLowerCase()) ||
            marker.driverName?.toString().toLowerCase().match(searchStopsMap.toLowerCase()) ||
            marker.name?.toString().toLowerCase().match(searchStopsMap.toLowerCase()) ||
            marker.orderCode?.toString().toLowerCase().match(searchStopsMap.toLowerCase()) ||
            marker.partner?.toString().toLowerCase().match(searchStopsMap.toLowerCase()) ||
            marker.volume?.toString().toLowerCase().match(searchStopsMap.toLowerCase())
          );
        });
      }
    }

    forEach(vehicles, (vehicle) => {
      const color = '#' + COLORS[vehicle.driver.id % (COLORS.length - 1)];
      if (hideDriverIds?.includes(vehicle.driver.id)) return;
      const steps = mapData[vehicle.driver.id]?.steps || [];

      const stepsPickup = steps.filter((step) => step.type === STEP_TYPE.pickup);

      let countPickup = 0;
      forEach(stepsPickup, (step) => {
        countPickup += step.orderIds?.length;
      });

      newMarkers.push({
        ...vehicle,
        id: `${vehicle.id}-start`,
        content: <Icon type="MarkerStart" color={'#fff'} strokeColor={color} />,
        latitude: Number(vehicle.startLat),
        longitude: Number(vehicle.startLng),
        offsetX: 1,
        offsetY: -6,
        countPickup,
        type: 'start',
        onClick: () => onClickStep({ id: `${vehicle.id}-start`, type: 'vehicle' }),
      });

      if (vehicle.endAddress && vehicle.endLat && vehicle.endLng) {
        newMarkers.push({
          ...vehicle,
          id: `${vehicle.id}-end`,
          content: <Icon type="MarkerEnd" color={'#fff'} strokeColor={color} />,
          latitude: Number(vehicle.endLat),
          longitude: Number(vehicle.endLng),
          offsetX: 1,
          offsetY: -6,
          type: 'end',
          onClick: () => onClickStep({ id: `${vehicle.id}-end`, type: 'vehicle' }),
        });
      }
    });

    return { markers: newMarkers, polylines: newPolylines };
  }, [mapData, vehicles, routes, hideDriverIds, allOrders, onClickStep, searchStopsMap, searchTicketsMap]);

  return <GoogleMap markers={markers} className="mapbox-routePlan-detail" polylines={polylines} active={active} />;
};

export default RouteMap;
